import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n";
import { PublicLayout, Section, Eyebrow } from "@/components/PublicLayout";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { centreCopy, checklistLabel, TONES } from "@/lib/centre";
import { Check, Lock, ArrowRight, FileText, CreditCard, Brain, MessageSquare, Award, Upload, Download, Send, Circle, Loader2 } from "lucide-react";

const L = {
  en: {
    profile: "Applicant profile", level: "Current education level", schooling: "Type of schooling",
    curriculum: "Curriculum followed", prior: "Previous school / institution", why: "Why Simo? (motivation)",
    save: "Save profile", choose: "Select…",
    docs: "Documents", nodocs: "No documents yet. Upload prior school reports, ID or references (PDF or image, max 15 MB).",
    upload: "Upload a document", uploading: "Uploading…",
    offer: "Offer & decision", pending: "Pending your assessment and review.", decision: "A decision is available — details are in your email.",
    paytuition: "Pay tuition for this programme",
    levels: [["prek", "Pre-K / Early years"], ["primary", "Primary"], ["lower_sec", "Lower secondary"], ["upper_sec", "Upper secondary"], ["foundation", "Foundation"], ["undergrad", "Undergraduate"], ["grad", "Graduate"], ["none", "Not currently enrolled"]],
    schoolings: [["traditional", "Traditional school"], ["homeschool", "Homeschooled"], ["hybrid", "Hybrid (school + home)"], ["online", "Online school"], ["none", "Not enrolled"]],
    curriculums: [["national", "National curriculum"], ["british", "British / Cambridge"], ["french", "French"], ["american", "American"], ["ib", "International Baccalaureate"], ["other", "Other"]],
  },
  fr: {
    profile: "Profil du candidat", level: "Niveau d'études actuel", schooling: "Type de scolarité",
    curriculum: "Programme suivi", prior: "École / établissement précédent", why: "Pourquoi Simo ? (motivation)",
    save: "Enregistrer le profil", choose: "Choisir…",
    docs: "Documents", nodocs: "Aucun document pour l'instant. Téléversez bulletins, pièce d'identité ou références (PDF ou image, 15 Mo max).",
    upload: "Téléverser un document", uploading: "Téléversement…",
    offer: "Offre & décision", pending: "En attente de votre évaluation et de l'examen.", decision: "Une décision est disponible — les détails sont dans votre e-mail.",
    paytuition: "Régler les frais de scolarité de ce programme",
    levels: [["prek", "Maternelle / Petite enfance"], ["primary", "Primaire"], ["lower_sec", "Collège"], ["upper_sec", "Lycée"], ["foundation", "Année préparatoire"], ["undergrad", "Licence"], ["grad", "Master / Doctorat"], ["none", "Non scolarisé actuellement"]],
    schoolings: [["traditional", "École traditionnelle"], ["homeschool", "Instruction à domicile"], ["hybrid", "Hybride (école + maison)"], ["online", "École en ligne"], ["none", "Non scolarisé"]],
    curriculums: [["national", "Programme national"], ["british", "Britannique / Cambridge"], ["french", "Français"], ["american", "Américain"], ["ib", "Baccalauréat International"], ["other", "Autre"]],
  },
};

const X = {
  en: {
    nextStep: "Your next step", checklist: "Your application checklist",
    reviewTitle: "Review & submit", reviewLead: "Your assessment is complete. Submit your application to send it for academic review.",
    submitBtn: "Submit my application", submitting: "Submitting…",
    submitBlocked: "The mandatory assessment must be completed before you can submit.",
    decisionTitle: "Admissions decision", viewDecision: "View decision", downloadPdf: "Download PDF (official letter)",
    payNow: "Pay tuition & confirm the place", updated: "There has been an update to your application.",
    submitted: "Submitted", underReview: "Your application is under academic review. No action is required.",
  },
  fr: {
    nextStep: "Votre prochaine étape", checklist: "Votre liste de vérification",
    reviewTitle: "Vérifier et soumettre", reviewLead: "Votre évaluation est terminée. Soumettez votre candidature pour l'envoyer à l'examen académique.",
    submitBtn: "Soumettre ma candidature", submitting: "Envoi…",
    submitBlocked: "L'évaluation obligatoire doit être terminée avant de pouvoir soumettre.",
    decisionTitle: "Décision d'admission", viewDecision: "Consulter la décision", downloadPdf: "Télécharger le PDF (lettre officielle)",
    payNow: "Régler les frais et confirmer la place", updated: "Une mise à jour de votre candidature est disponible.",
    submitted: "Soumise", underReview: "Votre candidature est en cours d'examen académique. Aucune action n'est requise.",
  },
};

function Sel({ testid, label, value, onChange, options, placeholder }) {
  return (
    <div>
      <label className="text-sm text-ink/60 block mb-1">{label}</label>
      <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-border rounded-sm px-3 py-2 bg-white text-ink focus:border-heraldic outline-none">
        <option value="">{placeholder}</option>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function Timeline({ items }) {
  return (
    <div className="flex flex-col gap-0" data-testid="app-timeline">
      {items.map((m, i) => (
        <div key={m.stage} className="flex gap-3">
          <div className="flex flex-col items-center">
            <div className={`h-4 w-4 rounded-full border-2 ${m.status === "done" ? "bg-heraldic border-heraldic" : m.status === "current" ? "bg-paper border-heraldic" : "bg-paper border-border"}`} />
            {i < items.length - 1 && <div className={`w-0.5 flex-1 ${m.status === "done" ? "bg-heraldic" : "bg-border"}`} style={{ minHeight: 28 }} />}
          </div>
          <div className={`pb-6 text-sm ${m.status === "pending" ? "text-ink/40" : "text-ink"}`}>
            {m.label}{m.status === "current" && <span className="ml-2 text-xs text-heraldic">current</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function Card({ icon: Icon, title, children, testid }) {
  return (
    <div data-testid={testid} className="border border-border rounded-sm p-6 bg-paper">
      <div className="flex items-center gap-2 mb-3"><Icon className="h-4 w-4 text-heraldic" /><div className="eyebrow text-ink/50">{title}</div></div>
      {children}
    </div>
  );
}

export default function ApplicationDetail() {
  const { appId } = useParams();
  const nav = useNavigate();
  const { lang } = useI18n();
  const t = L[lang] || L.en;
  const x = X[lang] || X.en;
  const [a, setA] = useState(null);
  const [profile, setProfile] = useState({ current_level: "", schooling_type: "", curriculum: "", prior_school: "", motivation: "" });
  const [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState(null);
  const [showDecision, setShowDecision] = useState(false);

  const load = () => api.get(`/applications/${appId}`).then((r) => {
    setA(r.data);
    setProfile({ current_level: "", schooling_type: "", curriculum: "", prior_school: "", motivation: "", ...(r.data.steps?.profile || {}) });
  }).catch(() => setA(false));
  useEffect(() => { load(); }, [appId]);

  if (a === false) return <PublicLayout><Section className="py-24"><p className="text-ink/50">Application not found.</p></Section></PublicLayout>;
  if (!a) return <PublicLayout><Section className="py-24"><p className="text-ink/50">Loading…</p></Section></PublicLayout>;

  const paid = a.payment_status === "paid";
  const tp = a.tuition_profile || {};
  const admitted = ["Offer sent", "Offer accepted", "Conditionally admitted", "Admitted", "Deposit paid", "Tuition active", "Onboarding", "Enrolled"].includes(a.stage);

  const saveProfile = async () => {
    await api.patch(`/applications/${appId}/section`, { section: "profile", data: profile });
    toast.success("Saved");
  };
  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { await api.post(`/applications/${appId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); await load(); toast.success("Uploaded"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const downloadDoc = async (d) => {
    try {
      if (d.storage_path) {
        const res = await api.get(`/files/${d.storage_path}`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const link = document.createElement("a"); link.href = url; link.download = d.name || "document"; link.click();
        URL.revokeObjectURL(url);
      }
    } catch { toast.error("Download failed"); }
  };
  const takeAssessment = async () => {
    setBusy(true);
    try {
      await api.post(`/assessment/${appId}/start`);
      nav(`/assessment/${appId}`);
    } catch (err) {
      if (err.response?.status === 402) { nav(`/apply/pay/${appId}`); }
      else toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const submitApplication = async () => {
    setBusy(true);
    try {
      await api.post(`/applications/${appId}/submit`, { confirm: true });
      toast.success(lang === "fr" ? "Candidature soumise — en cours d'examen." : "Application submitted — now under review.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const openDecision = async () => {
    try {
      const { data } = await api.get(`/applications/${appId}/decision`);
      setDecision(data);
      setShowDecision(true);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const downloadDecisionPdf = async () => {
    try {
      const res = await api.get(`/applications/${appId}/decision.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a"); link.href = url; link.download = `decision-${appId}.pdf`; link.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <PublicLayout>
      <Section className="py-14 md:py-20">
        <Link to="/centre" className="text-sm text-ink/50 hover:text-ink">← Application Centre</Link>
        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Eyebrow>{a.application_id}</Eyebrow>
            <h1 className="font-serif text-4xl tracking-tight text-ink" data-testid="detail-name">{a.applicant_name}</h1>
            <p className="mt-1 text-ink/55">{a.programme} · {a.market} market{a.guardian_name ? ` · Guardian: ${a.guardian_name}` : ""}</p>
          </div>
          <span className="text-sm px-3 py-1.5 rounded-full bg-heraldic/10 text-heraldic" data-testid="detail-stage">{a.stage}</span>
        </div>

        {/* State-driven next action banner */}
        {(() => {
          const copy = centreCopy(a.status_state, lang);
          const onCta = () => {
            if (a.status_state === "decision") return openDecision();
            if (a.status_state === "ready_to_submit") return submitApplication();
            if (["assessment_payment", "assessment_required", "assessment_in_progress"].includes(a.status_state)) return takeAssessment();
            const el = document.querySelector('[data-testid="section-profile"]');
            if (el) el.scrollIntoView({ behavior: "smooth" });
          };
          return (
            <div className={`mt-6 border-l-4 rounded-r-sm p-5 ${TONES[copy.tone]}`} data-testid="detail-next-action">
              <div className="eyebrow text-heraldic mb-1">{x.nextStep}</div>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="max-w-xl">
                  <div className="font-serif text-xl text-ink">{copy.title}</div>
                  <p className="mt-1 text-ink/75 text-sm">{copy.sub}</p>
                </div>
                {copy.cta && (
                  <button data-testid="detail-next-cta" disabled={busy} onClick={onCta}
                    className="inline-flex items-center gap-2 bg-heraldic text-white px-6 py-3 rounded-sm hover:bg-heraldic-700 transition-colors text-sm disabled:opacity-50 whitespace-nowrap">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} {copy.cta}
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* Decision viewer (rendered inside the Centre — email is only a notification) */}
        {a.has_decision && showDecision && decision && (
          <div className="mt-6 border border-heraldic/40 rounded-sm bg-paper p-6" data-testid="decision-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="eyebrow text-heraldic">{x.decisionTitle}</div>
              <div className="flex gap-2">
                <button data-testid="decision-download" onClick={downloadDecisionPdf} className="inline-flex items-center gap-1.5 text-sm bg-ink text-paper px-4 py-2 rounded-sm"><Download className="h-4 w-4" /> {x.downloadPdf}</button>
              </div>
            </div>
            <h3 className="mt-3 font-serif text-xl text-ink">{decision.subject}</h3>
            <div className="mt-3 prose prose-sm max-w-none text-ink/80" dangerouslySetInnerHTML={{ __html: decision.body_html }} />
            {decision.payment_link && (
              <a data-testid="decision-pay" href={decision.payment_link} className="mt-4 inline-flex items-center gap-2 bg-heraldic text-white px-5 py-2.5 rounded-sm text-sm"><CreditCard className="h-4 w-4" /> {x.payNow}</a>
            )}
          </div>
        )}

        <div className="mt-10 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Applicant profile — structured admissions questions */}
            <Card icon={FileText} title={t.profile} testid="section-profile">
              <div className="grid sm:grid-cols-2 gap-4">
                <Sel testid="profile-level" label={t.level} value={profile.current_level} onChange={(v) => setProfile({ ...profile, current_level: v })} options={t.levels} placeholder={t.choose} />
                <Sel testid="profile-schooling" label={t.schooling} value={profile.schooling_type} onChange={(v) => setProfile({ ...profile, schooling_type: v })} options={t.schoolings} placeholder={t.choose} />
                <Sel testid="profile-curriculum" label={t.curriculum} value={profile.curriculum} onChange={(v) => setProfile({ ...profile, curriculum: v })} options={t.curriculums} placeholder={t.choose} />
                <div>
                  <label className="text-sm text-ink/60 block mb-1">{t.prior}</label>
                  <input data-testid="profile-prior" value={profile.prior_school} onChange={(e) => setProfile({ ...profile, prior_school: e.target.value })} className="w-full border border-border rounded-sm px-3 py-2 bg-white text-ink focus:border-heraldic outline-none" />
                </div>
              </div>
              <label className="text-sm text-ink/60 mt-4 block">{t.why}</label>
              <Textarea data-testid="profile-motivation" value={profile.motivation} onChange={(e) => setProfile({ ...profile, motivation: e.target.value })} className="mt-1 rounded-sm" />
              <button data-testid="profile-save" onClick={saveProfile} className="mt-3 text-sm bg-ink text-paper px-4 py-2 rounded-sm">{t.save}</button>
            </Card>

            {/* Documents — real upload */}
            <Card icon={FileText} title={t.docs} testid="section-documents">
              {(a.documents || []).length === 0 && <p className="text-sm text-ink/45 mb-3">{t.nodocs}</p>}
              <ul className="space-y-1 mb-3">{(a.documents || []).map((d, i) => (
                <li key={i} className="text-sm text-ink/75 flex items-center gap-2">
                  <Check className="h-3.5 w-3.5 text-heraldic" />
                  <span className="flex-1 truncate">{d.name}{d.note ? ` — ${d.note}` : ""}</span>
                  {d.storage_path && <button data-testid={`doc-download-${i}`} onClick={() => downloadDoc(d)} className="text-heraldic hover:underline flex items-center gap-1"><Download className="h-3.5 w-3.5" /></button>}
                </li>))}</ul>
              <label data-testid="doc-upload" className="inline-flex items-center gap-2 bg-ink text-paper px-4 py-2 rounded-sm text-sm cursor-pointer hover:bg-heraldic transition-colors">
                <Upload className="h-4 w-4" /> {busy ? t.uploading : t.upload}
                <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" disabled={busy} onChange={onUpload} />
              </label>
            </Card>

            {/* Assessment */}
            <Card icon={Brain} title="Admissions assessment" testid="section-assessment">
              {a.status_state === "ready_to_submit" ? (
                <div>
                  <p className="text-sm text-ink/70 mb-3 flex items-center gap-1.5"><Check className="h-4 w-4 text-green-600" /> {x.reviewLead}</p>
                  <button data-testid="submit-application" disabled={busy} onClick={submitApplication} className="inline-flex items-center gap-2 bg-heraldic text-white px-6 py-3 rounded-sm hover:bg-heraldic-700 transition-colors disabled:opacity-50">{busy ? x.submitting : x.submitBtn} <Send className="h-4 w-4" /></button>
                </div>
              ) : (a.submitted || a.stage === "Under review") ? (
                <p className="text-sm text-ink/70">{x.underReview}</p>
              ) : paid ? (
                <div>
                  <p className="text-sm text-ink/70 mb-3">Your application is active. Take the adaptive assessment — one question at a time, resume any time.</p>
                  <button data-testid="start-assessment" disabled={busy} onClick={takeAssessment} className="inline-flex items-center gap-2 bg-heraldic text-white px-6 py-3 rounded-sm hover:bg-heraldic-700 transition-colors">Take the assessment <ArrowRight className="h-4 w-4" /></button>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-ink/70 mb-3 flex items-center gap-1.5"><Lock className="h-4 w-4 text-ink/40" /> The assessment unlocks after you activate your application.</p>
                  <button data-testid="activate-application" disabled={busy} onClick={takeAssessment} className="inline-flex items-center gap-2 bg-heraldic text-white px-6 py-3 rounded-sm hover:bg-heraldic-700 transition-colors">Activate my application — €97 <ArrowRight className="h-4 w-4" /></button>
                  <p className="mt-2 text-xs text-ink/45">Unlocks the secure assessment, your reasoning profile, programme-fit review and admissions follow-up.</p>
                </div>
              )}
            </Card>

            {/* Messages & offers */}
            <div className="grid sm:grid-cols-2 gap-6">
              <Card icon={MessageSquare} title="Admissions messages" testid="section-messages">
                <p className="text-sm text-ink/45">{(a.messages || []).length ? a.messages[a.messages.length - 1].text : "No messages yet."}</p>
              </Card>
              <Card icon={Award} title={t.offer} testid="section-offer">
                {a.has_decision ? (
                  <div>
                    <p className="text-sm text-ink/70 mb-3">{x.updated}</p>
                    <div className="flex flex-wrap gap-2">
                      <button data-testid="offer-view-decision" onClick={openDecision} className="inline-flex items-center gap-2 bg-ink text-paper px-5 py-2.5 rounded-sm hover:bg-heraldic transition-colors text-sm"><FileText className="h-4 w-4" /> {x.viewDecision}</button>
                      {admitted && <Link data-testid="pay-tuition-btn" to={`/apply/tuition/${appId}`} className="inline-flex items-center gap-2 bg-heraldic text-white px-5 py-2.5 rounded-sm hover:bg-heraldic-700 transition-colors text-sm"><CreditCard className="h-4 w-4" /> {t.paytuition}</Link>}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-ink/45">{t.pending}</p>
                )}
              </Card>
            </div>
          </div>

          {/* Right rail */}
          <aside className="space-y-6">
            <Card icon={CreditCard} title="Payments" testid="section-payments">
              <div className="flex justify-between text-sm"><span className="text-ink/60">Application fee</span><span className="font-mono">€97</span></div>
              <div className="mt-1 flex justify-between text-sm"><span className="text-ink/60">Status</span>
                <span data-testid="payment-status" className={paid ? "text-green-700" : "text-ink/60"}>{paid ? "Paid" : "Not yet paid"}</span></div>
              {tp.net != null && <div className="mt-3 pt-3 border-t border-border text-sm"><span className="text-ink/60">Indicative tuition</span><div className="font-serif text-2xl text-ink">€{tp.net.toLocaleString()}<span className="text-xs text-ink/40"> / year</span></div></div>}
            </Card>
            <Card icon={Check} title="Status timeline" testid="section-timeline">
              <Timeline items={a.timeline || []} />
            </Card>
            <Card icon={FileText} title={x.checklist} testid="section-checklist">
              <div className="space-y-2">
                {(a.checklist || []).map((c) => (
                  <div key={c.key} className={`flex items-center gap-2 text-sm ${c.done ? "text-ink" : "text-ink/45"}`} data-testid={`checklist-${c.key}`}>
                    {c.done ? <Check className="h-4 w-4 text-green-600" /> : <Circle className="h-4 w-4" />} {checklistLabel(c.key, lang)}
                  </div>
                ))}
              </div>
            </Card>
          </aside>
        </div>
      </Section>
    </PublicLayout>
  );
}
